import { getToken, deleteToken } from '../auth';
import { camelizeKeys } from 'humps';
import { normalize } from 'normalizr';

import { addEntities } from '../redux/actions';
import { LOGOUT } from '../redux/modules/user';

import CONFIG from '../config';

const queryParams = (params) => Object.keys(params)
        .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
        .join('&');

// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
export const callApi = (endpoint, schema, method="GET", payload={}, headers={}) => {
  let fullUrl = (endpoint.indexOf(CONFIG.API_ROOT) === -1) ? CONFIG.API_ROOT + endpoint : endpoint;

  let config = {
    method: method,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...headers
    },
  };
  
  const token = getToken();

  if (token) {
    config.headers.Authorization = `Token ${token}`;
  };

  if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
    config.body = JSON.stringify(payload)
  } else if (payload) {
    fullUrl += (fullUrl.indexOf('?') === -1 ? '?' : '&') + queryParams(payload);
  }

  return fetch(fullUrl, config)
    .then(response =>
      response.json().then(
        json => {
          const camelizedJson = camelizeKeys(json);
          if (!response.ok) {
            return Promise.reject({
              response,
              json: camelizedJson
            });
          }

          if (schema) {
            // paginated response
            if (Array.isArray(schema)) {
              return {...camelizedJson, ...normalize(camelizedJson.results, schema)};
            } else {
              return {...camelizedJson, ...normalize(camelizedJson, schema)};
            }

          } else {
            return camelizedJson;
          }
        },
        err => Promise.reject({response})
      )
    );
}



// Action key that carries API call info interpreted by this Redux middleware.
export const CALL_API = Symbol('Call API')

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default store => next => action => {
  const callAPI = action[CALL_API]
  if (typeof callAPI === 'undefined') {
    return next(action)
  }

  let { endpoint, method, payload } = callAPI
  const { schema, types, ...rest } = callAPI

  if (typeof endpoint === 'function') {
    endpoint = endpoint(store.getState())
  }

  if (typeof endpoint !== 'string') {
    throw new Error('Specify a string endpoint URL.')
  }

  // schemas are optional now
  // if (!schema) {
  //   throw new Error('Specify one of the exported Schemas.')
  // }

  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error('Expected an array of three action types.')
  }
  if (!types.every(type => typeof type === 'string')) {
    throw new Error('Expected action types to be strings.')
  }

  // @TODO validate method, payload

  const actionWith = data => {  
    const finalAction = Object.assign({}, action, data)
    delete finalAction[CALL_API]
    return finalAction
  }

  const [ requestType, successType, failureType ] = types
  next(actionWith({ type: requestType, ...rest }))


  return callApi(endpoint, schema, method, payload).then(
    response => {
      if (schema && !rest.skipAdd) {
        next(addEntities(response.entities))
      }

      return next(actionWith({
        response,
        type: successType
      }));
    },
    ({response, json}) => {
      if (response.status === 401 && json.detail && json.detail === "Invalid token.") {
        deleteToken();
        location.reload();
      } else {
        next(actionWith({
          type: failureType,
          error: json || 'Something bad happened'
        }));
      }
      return Promise.reject(json);
    }
  );

}


// WEBPACK FOOTER //
// ./src/middleware/api.js